import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import FundingLenderFilters from '../../components/funding/FundingLenderFilters';
import FundingLenderForm from '../../components/funding/FundingLenderForm';
import FundingLenderTable from '../../components/funding/FundingLenderTable';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingLenders } from '../../hooks/funding/useFundingLenders';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';

const FundingLendersPage = () => {
  const { isPlatformContext } = useFundingWorkspace();
  const { pathFor } = useFundingRoutes();
  const [search, setSearch] = useState('');
  const [lenderType, setLenderType] = useState('');
  const [productType, setProductType] = useState('');
  const [status, setStatus] = useState('');
  const [state, setState] = useState('');
  const [featured, setFeatured] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const filters = useMemo(() => ({
    search: search || undefined,
    lenderType: lenderType || undefined,
    productType: productType || undefined,
    status: status || undefined,
    state: state || undefined,
    featured: featured || undefined,
  }), [search, lenderType, productType, status, state, featured]);

  const { lenders, loading, saving, error, create } = useFundingLenders({ filters, autoLoad: true });

  const detailPathFor = (lenderId) => `${pathFor('lenders')}/${lenderId}`;

  return (
    <FundingSectionPage
      title="Lenders"
      subtitle="Catalog criteria, products, and historical performance for bank and lender partners."
      activeStage="match"
      actions={isPlatformContext ? (
        <Button className="rounded-xl" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Cancel' : 'New lender'}
        </Button>
      ) : null}
    >
      <FundingPanel title="Filters" subtitle="Search and refine the lender catalog">
        <FundingLenderFilters
          search={search}
          lenderType={lenderType}
          productType={productType}
          status={status}
          state={state}
          featured={featured}
          showStatus={isPlatformContext}
          onSearchChange={setSearch}
          onLenderTypeChange={setLenderType}
          onProductTypeChange={setProductType}
          onStatusChange={setStatus}
          onStateChange={setState}
          onFeaturedChange={setFeatured}
        />
      </FundingPanel>

      {showCreate && isPlatformContext ? (
        <FundingPanel title="New lender" subtitle="Platform catalog entry">
          <FundingLenderForm
            saving={saving}
            onCancel={() => setShowCreate(false)}
            onSubmit={async (payload) => {
              await create(payload);
              setShowCreate(false);
            }}
          />
        </FundingPanel>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      <FundingPanel title="Lender catalog" subtitle={isPlatformContext ? 'Manage active, paused, and archived lenders' : 'Active lenders available for matching'}>
        <FundingLenderTable lenders={lenders} loading={loading} detailPathFor={detailPathFor} />
      </FundingPanel>
    </FundingSectionPage>
  );
};

export default FundingLendersPage;
